<template>
  <div>
    <v-form v-if="!submitting" ref="form" validate-on="blur">
      <h3 class="mt-4">Delivery Address</h3>
      <!-- STRIPE ADDRESS ELEMENT -->
      <div class="form-group">
        <div ref="address-element" class="mt-2"></div>
        <v-text-field
          v-if="deliveryZoneStateLabel"
          :model-value="deliveryZoneStateLabel"
          label="State"
          readonly
          class="delivery-zone-field mt-4 mb-2"
        ></v-text-field>
        <!-- error display -->
        <p v-if="addressError" class="text-red">{{ addressError }}</p>
      </div>
      <h3 class="mt-4 mb-2">Contact Information</h3>
      <div class="form-group">
        <v-text-field
          v-model="form.email"
          label="Customer Email"
          :rules="[$rules.required('Customer Email'), $rules.email('Customer Email')]"
          required
        >
        </v-text-field>
        <v-text-field
          v-model="form.phone1"
          label="Mobile Number"
          :rules="[$rules.required('Phone')]"
          :error-messages="phoneError"
          required
        >
        </v-text-field>
      </div>
      <div class="mt-5 d-flex justify-end">
        <v-btn color="accent" variant="flat" class="mx-0" @click="submit">{{
          foc ? 'Place Order' : 'Continue to Payment'
        }}</v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import uid from 'uid'
import { validateUsZipcode, parseAndValidateUsMobile } from '@/js/validators'
import { db } from '@/js/db'
import { doc, getDoc } from 'firebase/firestore'

export default {
  name: 'ContactInformationUS',
  props: ['form', 'foc', 'elements', 'stripeObjects', 'quote', 'version'],
  data() {
    return {
      uid: uid,
      addressError: null,
      submitting: false,
      calculatingSalesTax: false,
      addressDetails: null,
      googleMapsApiRetrieved: false,
      deliveryZoneState: null,
      phoneError: []
    }
  },
  computed: {
    deliveryZoneStateLabel() {
      if (!this.deliveryZoneState) return null
      const match = this.$utils.usStateCodes.find((s) => s.value === this.deliveryZoneState)
      return match ? match.text : this.deliveryZoneState
    }
  },
  mounted() {
    this.mountAddress()
  },
  beforeUnmount() {
    this.unmountAddress()
  },
  methods: {
    async mountAddress() {
      await this.$nextTick()

      // Mount the address element first so a failure of either lookup below never blocks the form.
      let lastOrder = null
      if (this.quote.orders && Object.values(this.quote.orders).length > 0) {
        try {
          const orders = Object.values(this.quote.orders)
          lastOrder = await getDoc(doc(db, 'orders', orders[orders.length - 1].orderID))
        } catch (e) {
          // prepopulation is best-effort
        }
      }
      this.setupAddressElement('address-element', lastOrder)

      // Delivery-zone guard is best-effort: if the read fails the submit-time check is skipped.
      if (this.version && this.version.deliveryLocation) {
        try {
          const zone = await getDoc(doc(db, 'shippingZones', this.version.deliveryLocation))
          if (zone.exists() && zone.data().state) {
            this.deliveryZoneState = zone.data().state
          }
        } catch (e) {
          // ignore — form remains usable without the guard
        }
      }
    },
    unmountAddress() {
      this.unmountAddressElement('address-element')
    },
    setupAddressElement(el, lastOrder) {
      let allowedCountriesArray = ['US']
      let placesKey
      if (window.location.hostname == 'mega.plykea.com') {
        placesKey = 'AIzaSyAv4aRIjITY5teVksdZiTZkJiYW2-AtkjU' //prod
      } else {
        placesKey = 'AIzaSyCIUq0Y4cXiT9oIsPN4JReKvOS1C5QQf58' //dev
      }
      let addressOptions = {
        mode: 'shipping',
        autocomplete: {
          mode: 'google_maps_api',
          apiKey: placesKey
        },
        allowedCountries: allowedCountriesArray,
        blockPoBox: true,
        display: {
          name: 'split'
        }
      }

      if (lastOrder) {
        this.addressDetails = {
          firstName: lastOrder.data().customerContactDetails.firstName,
          lastName: lastOrder.data().customerContactDetails.lastName,
          address: {
            line1: lastOrder.data().customerContactDetails.deliveryAddressLine1,
            line2: lastOrder.data().customerContactDetails.deliveryAddressLine2,
            city: lastOrder.data().customerContactDetails.deliveryCity,
            state: lastOrder.data().customerContactDetails.deliveryState,
            postal_code: lastOrder.data().customerContactDetails.deliveryPostcode,
            country: lastOrder.data().customerContactDetails.deliveryCountry
          }
        }

        // addressOptions in stripe Address Element needs defaultValue object
        addressOptions.defaultValues = this.addressDetails
      }

      // Create and mount the Address Element in shipping mode
      this.stripeObjects[el] = this.elements.create('address', addressOptions)
      this.stripeObjects[el].mount(this.$refs[el])
    },
    unmountAddressElement(el) {
      if (!this.stripeObjects[el]) return
      try {
        this.stripeObjects[el].destroy()
      } catch (e) {
        /* already destroyed */
      }
      this.stripeObjects[el] = null
    },
    async submit() {
      try {
        if (this.preferredDeliveryDate) {
          this.form.preferredDeliveryDate = this.$utils.timestampFromVuetify(this.preferredDeliveryDate)
        }

        const addressElement = this.elements.getElement('address')
        const deliveryResult = await addressElement.getValue()

        if (
          deliveryResult.complete &&
          deliveryResult.value.address.line1 &&
          deliveryResult.value.address.city &&
          deliveryResult.value.address.state &&
          deliveryResult.value.address.postal_code &&
          deliveryResult.value.address.country === 'US' // Ensure it's a US address
        ) {
          this.addressError = null // Clear any previous errors
          this.addressDetails = deliveryResult.value
        } else {
          this.addressError = 'Please complete all required billing address fields'
          return
        }

        if (this.deliveryZoneState && this.addressDetails.address.state !== this.deliveryZoneState) {
          const match = this.$utils.usStateCodes.find((s) => s.value === this.deliveryZoneState)
          const stateLabel = match ? match.text : this.deliveryZoneState
          this.addressError = `Delivery must be in ${stateLabel}`
          return
        }

        // Validate postcode explicitly here:
        const isZipcodeValid = await validateUsZipcode(
          this.addressDetails.address.postal_code,
          this.addressDetails.address.state
        )
        if (!isZipcodeValid) {
          this.addressError = 'Invalid US zipcode'
          return
        } else {
          this.addressError = null // Clear error if zipcode is valid
        }

        this.phoneError = []
        const isValid = await this.$utils.validateForm(this.$refs.form)
        if (!isValid) return

        // Normalize mobile number
        const formattedMobile = parseAndValidateUsMobile(this.form.phone1)
        if (!formattedMobile) {
          this.phoneError = ['Please enter a valid US mobile number']
          return
        }
        this.form.mobile = formattedMobile
        this.form.email = this.form.email.toLowerCase()

        this.$emit('success', this.form, this.addressDetails)
      } catch (error) {
        // console.error('Submit error:', error)
        // Was errors.add({ field: 'form' }) — but nothing ever read errors.collect('form'),
        // so a thrown submit has always failed silently here. Left as-is rather than made
        // visible, which would be a behaviour change. Flagged for QA. See §10.
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .delivery-zone-field input {
  font-size: 16px;
  color: #30313d;
  -webkit-text-fill-color: #30313d;
}
</style>
